import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getAIResponse, AIStatus } from "../services/AIServices";
import ZIIcon from "./ZAIIcon";
import AutoRunBotIcon from "./AutoRunBotIcon";
import { FaSpinner, FaChartLine, FaChartBar, FaInfoCircle, FaSyncAlt, FaTimes, FaBrain } from 'react-icons/fa';
import { formatDistanceToNow } from 'date-fns';

const POLL_INTERVAL = 30000;

const LoadingSpinner = ({ size = 'md', className = '' }) => (
  <div className={`flex items-center justify-center ${className}`}>
    <FaSpinner className={`animate-spin ${size === 'sm' ? 'h-4 w-4' : 'h-5 w-5'} text-blue-500`} />
  </div>
);

const formatTimeAgo = (dateString) => {
  try {
    if (!dateString) return 'Just now';
    
    // Handle both string and Date objects
    const date = typeof dateString === 'string' || dateString instanceof String
      ? new Date(dateString)
      : dateString;
      
    // Check if date is valid
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      console.warn('Invalid date value:', dateString);
      return 'Just now';
    }
    
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (error) {
    console.error('Error formatting date:', error, 'Date string:', dateString);
    return 'Just now';
  }
};

const PredictionCard = ({ direction, confidence, timestamp, isLoading }) => {
  const isUp = direction === 'UP';
  const upConfidence = parseFloat(confidence?.up || '0');
  const downConfidence = parseFloat(confidence?.down || '0');
  
  return (
    <div className="bg-gray-800/50 rounded-xl border border-gray-700/50 overflow-hidden transition-all duration-300 hover:shadow-lg">
      <div className="p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className={`p-3 rounded-xl ${isUp ? 'bg-green-900/30' : 'bg-red-900/30'} border ${isUp ? 'border-green-500/30' : 'border-red-500/30'}`}>
              <FaChartLine className={`h-6 w-6 ${isUp ? 'text-green-400' : 'text-red-400'}`} />
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-300">Market Prediction</h3>
              <div className="flex items-center space-x-2 mt-1">
                <span className={`text-2xl font-bold ${isUp ? 'text-green-400' : 'text-red-400'}`}>
                  {direction} {isUp ? '↑' : '↓'}
                </span>
                {isLoading && <LoadingSpinner size="sm" className="ml-2" />}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-400">Confidence</div>
            <div className="text-xl font-bold text-white">
              {isUp ? (confidence.up * 100).toFixed(2) : (confidence.down * 100).toFixed(2)}%
            </div>
          </div>
        </div>
        
        <div className="mt-5 space-y-2">
          <div className="flex justify-between text-sm text-gray-400">
            <span>UP {(confidence.up * 100).toFixed(2)}%</span>
            <span>{(confidence.down * 100).toFixed(2)}% DOWN</span>
          </div>
          <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
            <div 
              className={`h-full ${isUp ? 'bg-gradient-to-r from-green-500 to-green-400' : 'bg-gradient-to-r from-red-500 to-red-400'}`}
              style={{ width: isUp ? `${upConfidence}%` : `${downConfidence}%` }}
            />
          </div>
        </div>
      </div>
      
      <div className="px-5 py-3 bg-gray-800/50 border-t border-gray-700/50 text-xs text-gray-400">
        <div className="flex justify-between items-center">
          <span>Updated {formatTimeAgo(timestamp)}</span>
          <button className="text-blue-400 hover:text-blue-300 transition-colors flex items-center">
            <FaInfoCircle className="mr-1" /> Details
          </button>
        </div>
      </div>
    </div>
  );
};

const EventOutcomeBadge = ({ eventType }) => {
  const isWin = eventType === 'WIN';
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
      isWin 
        ? 'bg-green-100 text-green-800' 
        : 'bg-red-100 text-red-800'
    }`}>
      {isWin ? (
        <>
          <svg className="-ml-0.5 mr-1.5 h-2 w-2 text-green-500" fill="currentColor" viewBox="0 0 8 8">
            <circle cx="4" cy="4" r="3" />
          </svg>
          WIN
        </>
      ) : (
        <>
          <svg className="-ml-0.5 mr-1.5 h-2 w-2 text-red-500" fill="currentColor" viewBox="0 0 8 8">
            <circle cx="4" cy="4" r="3" />
          </svg>
          LOSS
        </>
      )}
    </span>
  );
};

const EventStatusCard = ({ event }) => {
  if (!event) return null;
  
  const isWin = event.event_type === 'WIN';
  const isLoss = event.event_type === 'LOSS';
  const timeAgo = formatTimeAgo(event.created_at);
  
  return (
    <div className="bg-gray-800/50 rounded-xl border border-gray-700/50 overflow-hidden">
      <div className="p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-gray-200">Latest Trade</h3>
          <EventOutcomeBadge eventType={event.event_type} />
        </div>
        
        <div className="mt-3 grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-400">Prediction</p>
            <p className={`text-sm font-medium ${event.predicted_direction === 'UP' ? 'text-green-400' : 'text-red-400'}`}>
              {event.predicted_direction} {event.predicted_direction === 'UP' ? '↑' : '↓'}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Confidence</p>
            <p className="text-sm font-medium text-gray-200">
              {event.predicted_direction === 'UP' 
                ? `${event.confidence.up}%` 
                : `${event.confidence.down}%`}
            </p>
          </div>
        </div>
        
        <div className="mt-4 pt-3 border-t border-gray-700/50">
          <p className="text-xs text-gray-400">
            {isWin && (
              <span className="text-green-400">
                <span className="font-medium">Success:</span> The prediction was correct.
              </span>
            )}
            {isLoss && (
              <span className="text-red-400">
                <span className="font-medium">Note:</span> The prediction was incorrect.
              </span>
            )}
            {' '}
            <span className="text-gray-500">• {timeAgo}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

const TrainingStatus = ({ message, lastTrained }) => (
  <div className="bg-yellow-900/20 border-l-4 border-yellow-400 p-4 rounded-lg">
    <div className="flex items-start">
      <div className="flex-shrink-0 pt-0.5">
        <FaChartBar className="h-5 w-5 text-yellow-400" />
      </div>
      <div className="ml-3">
        <h3 className="text-yellow-200 text-sm font-medium">Model Training in Progress</h3>
        <p className="text-yellow-100 text-sm mt-1">
          {message || 'The AI model is currently being trained with the latest market data.'}
        </p>
        {lastTrained && (
          <p className="text-yellow-500/80 text-xs mt-2">
            Last trained: {new Date(lastTrained).toLocaleString()}
          </p>
        )}
      </div>
    </div>
  </div>
);

const WinStatusBadge = () => (
  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
    <svg className="-ml-0.5 mr-1.5 h-2 w-2 text-green-500" fill="currentColor" viewBox="0 0 8 8">
      <circle cx="4" cy="4" r="3" />
    </svg>
    SUCCESS
  </span>
);

const SuccessPredictionCard = ({ direction, confidence, timestamp, message }) => {
  const isUp = direction === 'UP';
  const timeAgo = formatTimeAgo(timestamp);
  
  return (
    <div className="bg-gray-800/50 rounded-xl border border-green-900/50 overflow-hidden">
      <div className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-200">Prediction Result</h3>
            <p className="text-green-400 text-sm mt-1">
              {message || 'The last prediction was successful!'}
            </p>
          </div>
          <WinStatusBadge />
        </div>
        
        <div className="mt-4 grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-400">Direction</p>
            <p className={`text-lg font-bold ${isUp ? 'text-green-400' : 'text-red-400'}`}>
              {direction} {isUp ? '↑' : '↓'}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Confidence</p>
            <p className="text-lg font-bold text-white">
              {isUp ? confidence.up : confidence.down}
            </p>
          </div>
        </div>
        
        <div className="mt-4 pt-3 border-t border-gray-700/50">
          <div className="flex justify-between items-center">
            <p className="text-xs text-gray-400">
              Predicted {timeAgo}
            </p>
            <button 
              className="text-xs text-blue-400 hover:text-blue-300 flex items-center"
              onClick={() => {}}
            >
              <FaInfoCircle className="mr-1" /> Details
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ThinkingAnimation = ({ status = 'Analyzing market data' }) => {
    const [currentWord, setCurrentWord] = useState(0);
    const [isVisible, setIsVisible] = useState(true);
    
    const statusMessages = [
        'Analyzing market trends',
        'Processing historical data',
        'Identifying patterns',
        'Updating prediction model',
        'Calculating probabilities',
        'Evaluating market conditions',
        'Optimizing parameters',
        'Generating insights'
    ];

    useEffect(() => {
        const wordInterval = setInterval(() => {
            setIsVisible(false);
            setTimeout(() => {
                setCurrentWord((prev) => (prev + 1) % statusMessages.length);
                setIsVisible(true);
            }, 200); // Fade out duration
        }, 2000); // Change word every 2 seconds

        return () => clearInterval(wordInterval);
    }, []);

    return (
        <div className="space-y-6">
            <div className="bg-gray-800/50 rounded-xl border border-blue-900/30 overflow-hidden">
                <div className="p-5">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className="flex space-x-1">
                                <div className="h-2 w-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                <div className="h-2 w-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                <div className="h-2 w-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                            </div>
                            <h3 className="text-sm font-medium text-blue-400">AI is thinking</h3>
                        </div>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-900/30 text-blue-300 border border-blue-800/50">
                            PROCESSING
                        </span>
                    </div>
                    
                    <div className="mt-4">
                        <div className="h-6 flex items-center">
                            <p className={`text-sm text-gray-300 mb-2 transition-opacity duration-200 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
                                {statusMessages[currentWord]}
                                <span className="ml-1 inline-block w-1 h-4 bg-blue-400 animate-pulse"></span>
                            </p>
                        </div>
                        <div className="w-full bg-gray-700/50 rounded-full h-1.5 overflow-hidden">
                            <div 
                                className="bg-gradient-to-r from-blue-500 to-blue-400 h-1.5 rounded-full transition-all duration-1000"
                                style={{
                                    width: '75%',
                                    backgroundSize: '200% 100%',
                                    animation: 'shimmer 2s infinite linear',
                                }}
                            ></div>
                        </div>
                    </div>
                </div>
            </div>
            
            <div className="bg-gray-800/30 p-4 rounded-xl border border-gray-700/30">
                <div className="flex items-start">
                    <FaBrain className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="ml-3">
                        <h4 className="text-sm font-medium text-gray-200">Processing Market Data</h4>
                        <p className="text-sm text-gray-300 mt-1">
                            Our AI is analyzing real-time market conditions and historical patterns to generate accurate predictions.
                        </p>
                        <div className="mt-2 flex items-center text-xs text-amber-400">
                            <svg className="h-3.5 w-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <span>This might take some time. Please wait...</span>
                        </div>
                    </div>
                </div>
            </div>
            
            <style jsx global>{`
                @keyframes shimmer {
                    0% { background-position: 200% 0; }
                    100% { background-position: -200% 0; }
                }
            `}</style>
        </div>
    );
};

const AIModal = ({ isOpen, onClose }) => {
    const [response, setResponse] = useState(null);
    const [aiStatus, setAiStatus] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);

    const fetchAIResponse = useCallback(async () => {
        try {
            const response = await getAIResponse();
            console.log('API Response:', response); // Debug log
            
            if (response.success) {
                setResponse(response);
                setError(null);
                
                // Handle different response types
                if (response.type === 'info' && response.data?.data?.event_type) {
                    const eventData = response.data.data;
                    console.log('Event Data:', eventData); // Debug log
                    
                    // Update status based on event type
                    if (eventData.event_type === 'TRAIN') {
                        setAiStatus('training');
                    } else if (eventData.event_type === 'WIN' || eventData.event_type === 'LOSS') {
                        setAiStatus('prediction');
                    }
                } else if (response.type === 'win' || response.type === 'success') {
                    setAiStatus('success');
                } else {
                    setAiStatus('prediction');
                }
                
                setLastUpdated(new Date());
            } else {
                setError(response.message || 'Failed to fetch AI response');
            }
        } catch (err) {
            console.error('Error fetching AI response:', err);
            setError('Failed to connect to the prediction service');
        } finally {
            setIsLoading(false);
            setIsRefreshing(false);
        }
    }, []);

    const handleRefresh = async () => {
        setIsRefreshing(true);
        await fetchAIResponse();
    };

    useEffect(() => {
        if (isOpen) {
            fetchAIResponse();
            const interval = setInterval(fetchAIResponse, POLL_INTERVAL);
            return () => clearInterval(interval);
        }
    }, [isOpen, fetchAIResponse]);

    const renderContent = () => {
        if (isLoading) {
            return (
                <div className="flex items-center justify-center py-12">
                    <div className="animate-pulse flex flex-col items-center">
                        <div className="h-12 w-12 bg-blue-900/30 rounded-full mb-4"></div>
                        <div className="h-4 w-32 bg-gray-700 rounded"></div>
                    </div>
                </div>
            );
        }

        if (error) {
            return (
                <div className="bg-red-900/20 border-l-4 border-red-500 p-5 rounded-lg">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-red-200">{error}</p>
                        </div>
                    </div>
                </div>
            );
        }

        // Handle WIN event type
        if ((response?.type === 'info' || response?.success) && (response?.data?.event_type === 'WIN' || response?.data?.data?.event_type === 'WIN')) {
            const eventData = response.data.event_type ? response.data : response.data.data;
            return (
                <div className="space-y-5">
                    <div className="bg-green-900/20 border-l-4 border-green-500 p-5 rounded-lg">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <h3 className="text-green-200 text-sm font-medium">Successful Prediction!</h3>
                                <div className="mt-2 text-green-100 text-sm">
                                    <p>The AI correctly predicted the market would go <span className="font-bold">{eventData.predicted_direction}</span></p>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="bg-gray-800/50 p-4 rounded-xl border border-gray-700/50">
                        <div className="flex items-start">
                            <FaChartLine className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                            <div className="ml-3">
                                <h4 className="text-sm font-medium text-gray-200">Prediction Details</h4>
                                <div className="mt-2 space-y-2 text-sm text-gray-300">
                                    <p>• Direction: <span className="font-mono">{eventData.predicted_direction}</span></p>
                                    <p>• Confidence: <span className="font-mono">
                                        {eventData.confidence ? 
                                            `${(eventData.confidence.up * 100).toFixed(2)}% UP / ${(eventData.confidence.down * 100).toFixed(2)}% DOWN` : 
                                            'N/A'}
                                    </span></p>
                                    <p>• Time: <span className="font-mono">{formatTimeAgo(eventData.created_at)}</span></p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        // Handle prediction event type
        if ((response?.type === 'info' || response?.success) && (response?.data?.event_type === 'PREDICT' || response?.data?.data?.event_type === 'PREDICT')) {
            const eventData = response.data.event_type ? response.data : response.data.data;
            return (
                <div className="space-y-5">
                    <PredictionCard 
                        direction={eventData.direction || eventData.predicted_direction}
                        confidence={eventData.confidence}
                        timestamp={eventData.created_at}
                        isLoading={isRefreshing}
                    />
                    <div className="bg-gray-800/50 p-4 rounded-xl border border-gray-700/50">
                        <div className="flex items-start">
                            <FaInfoCircle className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                            <div className="ml-3">
                                <h4 className="text-sm font-medium text-gray-200">Market Prediction</h4>
                                <p className="text-sm text-gray-300 mt-1">
                                    {response.message || 'The AI has analyzed the market and made a prediction.'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        // Handle training event type with the new thinking animation
        if ((response?.type === 'info' || response?.success) && (response?.data?.event_type === 'TRAIN' || response?.data?.data?.event_type === 'TRAIN')) {
            const statusMessages = [
                'Analyzing market microstructure and order flow...',
                'Processing high-frequency tick data for pattern recognition...',
                'Identifying statistical arbitrage opportunities...',
                'Optimizing neural network hyperparameters...',
                'Calculating volatility surfaces and Greeks...',
                'Evaluating market microstructure impact...',
                'Running Monte Carlo simulations...',
                'Generating probabilistic forecasts...',
                'Cross-validating model performance...',
                'Refining algorithmic trading parameters...',
                'Adjusting for market regime changes...',
                'Processing real-time order book data...',
                'Updating Bayesian inference models...',
                'Validating statistical significance...',
                'Finalizing quantitative analysis...'
            ];
            
            return (
                <div className="space-y-5">
                    <div className="bg-gradient-to-r from-gray-900 to-gray-800 border-l-4 border-blue-500 p-6 rounded-lg shadow-xl">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center space-x-3">
                                <div className="relative">
                                    <div className="absolute -inset-1 bg-blue-500/20 rounded-full blur opacity-75"></div>
                                    <div className="relative flex space-x-1.5">
                                        <div className="h-2 w-2 bg-blue-400 rounded-full animate-pulse" style={{ animationDelay: '0ms' }}></div>
                                        <div className="h-2 w-2 bg-blue-400 rounded-full animate-pulse" style={{ animationDelay: '200ms' }}></div>
                                        <div className="h-2 w-2 bg-blue-400 rounded-full animate-pulse" style={{ animationDelay: '400ms' }}></div>
                                    </div>
                                </div>
                                <h3 className="text-blue-100 text-sm font-semibold tracking-wide">MARKET ANALYSIS IN PROGRESS</h3>
                            </div>
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-900/30 text-blue-300 border border-blue-700/50 shadow">
                                <span className="relative flex h-2 w-2 mr-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                                </span>
                                LIVE
                            </span>
                        </div>
                        
                        <div className="space-y-4">
                            <div className="flex items-center space-x-3">
                                <div className="flex-shrink-0">
                                    <div className="h-10 w-10 rounded-full bg-blue-900/50 flex items-center justify-center border border-blue-800/50">
                                        <svg className="h-5 w-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                        </svg>
                                    </div>
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-blue-100 truncate">
                                        {statusMessages[Math.floor(Math.random() * statusMessages.length)]}
                                    </p>
                                    <div className="mt-1.5 w-full bg-gray-700/50 rounded-full h-1.5 overflow-hidden">
                                        <div 
                                            className="bg-gradient-to-r from-blue-500 to-cyan-400 h-full rounded-full transition-all duration-1000"
                                            style={{
                                                width: `${75 + Math.random() * 20}%`,
                                                backgroundSize: '200% 100%',
                                                animation: 'shimmer 2s infinite linear',
                                                boxShadow: '0 0 10px rgba(96, 165, 250, 0.5)'
                                            }}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="mt-4 pt-4 border-t border-gray-700/50">
                                <div className="flex items-center justify-between text-xs text-gray-400 mb-2">
                                    <span>Analysis Progress</span>
                                    <span>{Math.floor(75 + Math.random() * 20)}%</span>
                                </div>
                                <div className="relative">
                                    <div className="overflow-hidden h-1.5 text-xs flex rounded bg-gray-800/50">
                                        <div 
                                            className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-gradient-to-r from-blue-500 to-cyan-400 transition-all duration-1000 ease-in-out"
                                            style={{
                                                width: `${75 + Math.random() * 20}%`,
                                                backgroundSize: '200% 100%',
                                                animation: 'shimmer 2s infinite linear',
                                                boxShadow: '0 0 8px rgba(96, 165, 250, 0.6)'
                                            }}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 p-5 rounded-xl border border-gray-700/30 backdrop-blur-sm">
                        <div className="flex items-start">
                            <div className="flex-shrink-0">
                                <div className="h-10 w-10 rounded-full bg-blue-900/30 flex items-center justify-center border border-blue-800/30">
                                    <svg className="h-5 w-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                            </div>
                            <div className="ml-3 flex-1">
                                <h4 className="text-sm font-medium text-gray-200">Market Analysis Underway</h4>
                                <p className="mt-1 text-sm text-gray-300 leading-relaxed">
                                    Our quantitative analysis engine is processing multiple data streams, including order flow, liquidity metrics, and market microstructure indicators to generate high-probability trading signals.
                                </p>
                                <div className="mt-3 flex items-center text-xs text-amber-400/90">
                                    <svg className="h-3.5 w-3.5 mr-1.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    <span>Processing may take 15-30 seconds. Please do not refresh the page.</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        // Handle win/success prediction
        if (response?.type === 'win' || response?.type === 'success') {
            return (
                <div className="space-y-5">
                    <SuccessPredictionCard 
                        direction={response.direction}
                        confidence={response.confidence}
                        timestamp={response.timestamp}
                        message={response.message}
                    />
                </div>
            );
        }

        // Handle regular prediction
        if (response?.type === 'info' && response.data?.direction) {
            return (
                <div className="space-y-5">
                    <PredictionCard 
                        direction={response.data.direction}
                        confidence={response.data.confidence || { up: '0%', down: '0%' }}
                        timestamp={response.data.timestamp}
                        isLoading={isRefreshing}
                    />
                </div>
            );
        }

        // Default fallback
        return (
            <div className="text-center py-8 text-gray-400">
                <FaChartLine className="mx-auto h-12 w-12 mb-4 opacity-30" />
                <p>No prediction data available</p>
                <p className="text-sm mt-2">Check back later for updates</p>
            </div>
        );
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40"
                    />

                    <motion.div
                        initial={{ scale: 0.96, opacity: 0, y: 10 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.96, opacity: 0, y: 10 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        className="fixed inset-0 flex items-center justify-center z-50 p-4"
                    >
                        <div className="bg-gray-900 rounded-2xl shadow-2xl border border-gray-800 w-full max-w-md relative overflow-hidden">
                            {/* Header */}
                            <div className={`p-5 border-b ${aiStatus?.is_active ? 'border-green-900/30' : 'border-gray-800'}`}>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                        <ZIIcon />
                                        <div>
                                            <h2 className="text-xl font-bold text-white">ZIZeus M8</h2>
                                            <div className="flex items-center space-x-2 mt-0.5">
                                                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-900/50 text-blue-300">
                                                    {aiStatus?.is_active ? 'ACTIVE' : 'INACTIVE'}
                                                </span>
                                                <span className="text-xs text-gray-400">AI Trading Assistant</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <button
                                            onClick={handleRefresh}
                                            disabled={isRefreshing}
                                            className="p-1.5 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-blue-400 transition-colors"
                                            aria-label="Refresh data"
                                        >
                                            <FaSyncAlt className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                                        </button>
                                        <button
                                            onClick={onClose}
                                            className="p-1.5 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors"
                                            aria-label="Close"
                                        >
                                            <FaTimes className="h-5 w-5" />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="p-5 max-h-[70vh] overflow-y-auto">
                                {renderContent()}
                            </div>

                            {/* Footer */}
                            <div className="p-3 bg-gray-900/80 border-t border-gray-800 text-center">
                                <div className="flex items-center justify-between text-xs text-gray-500">
                                    <div className="flex items-center space-x-2">
                                        {aiStatus?.is_active && (
                                            <span className="flex items-center">
                                                <span className="h-2 w-2 bg-green-500 rounded-full mr-1.5"></span>
                                                <span>AI Active</span>
                                            </span>
                                        )}
                                    </div>
                                    <div>
                                        {lastUpdated && (
                                            <span className="text-gray-500">
                                                Updated {formatTimeAgo(lastUpdated)}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default AIModal;